import { Calculator, ChartBar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

export const ROICalculator = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    patientsPerMonth: "",
    averageAssessmentTime: "",
    hourlyStaffCost: "",
    currentErrorRate: "",
  });
  const [results, setResults] = useState<{
    annualSavings: number;
    timeReduction: number;
    qualityImprovement: number;
  } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateROI = () => {
    // Validate inputs
    const values = Object.values(formData);
    if (values.some(value => !value || isNaN(Number(value)))) {
      toast({
        title: "Invalid Input",
        description: "Please fill in all fields with valid numbers",
        variant: "destructive"
      });
      return;
    }

    // Convert string inputs to numbers
    const patients = Number(formData.patientsPerMonth);
    const assessmentTime = Number(formData.averageAssessmentTime);
    const staffCost = Number(formData.hourlyStaffCost);
    const errorRate = Number(formData.currentErrorRate);

    // Calculate time savings
    const timeReduction = 0.35; // 35% reduction in assessment time
    const monthlyTimeSaved = patients * assessmentTime * timeReduction;
    const annualTimeSaved = monthlyTimeSaved * 12;

    // Calculate cost savings
    const hourlyTimeValue = staffCost;
    const annualCostSavings = (annualTimeSaved / 60) * hourlyTimeValue;

    // Calculate quality improvement
    const qualityImprovement = errorRate * 0.45; // 45% reduction in errors

    // Calculate total annual savings
    const annualSavings = annualCostSavings + (qualityImprovement * patients * 12 * 250); // $250 assumed cost per error

    setResults({
      annualSavings: Math.round(annualSavings),
      timeReduction: Math.round(annualTimeSaved),
      qualityImprovement: Math.round(qualityImprovement * 100)
    });

    toast({
      title: "ROI Calculation Complete",
      description: "Your potential savings have been calculated.",
    });
  };

  return (
    <section className="py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            ROI Calculator
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calculate the potential return on investment for your organization
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <Card className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Calculator className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold">Input Parameters</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Patient Assessments
                </label>
                <Input
                  type="number"
                  name="patientsPerMonth"
                  value={formData.patientsPerMonth}
                  onChange={handleInputChange}
                  placeholder="e.g., 100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Assessment Time (minutes)
                </label>
                <Input
                  type="number"
                  name="averageAssessmentTime"
                  value={formData.averageAssessmentTime}
                  onChange={handleInputChange}
                  placeholder="e.g., 45"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff Hourly Cost ($)
                </label>
                <Input
                  type="number"
                  name="hourlyStaffCost"
                  value={formData.hourlyStaffCost}
                  onChange={handleInputChange}
                  placeholder="e.g., 75"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Error Rate (%)
                </label>
                <Input
                  type="number"
                  name="currentErrorRate"
                  value={formData.currentErrorRate}
                  onChange={handleInputChange}
                  placeholder="e.g., 5"
                />
              </div>
              <Button className="w-full mt-6" onClick={calculateROI}>
                Calculate Savings
              </Button>
            </div>
          </Card>

          <Card className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-primary/10 p-3 rounded-lg">
                <ChartBar className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold">Projected Results</h3>
            </div>
            {results ? (
              <div className="space-y-6">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Annual Cost Savings</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${results.annualSavings.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Annual Time Saved (minutes)</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {results.timeReduction.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Error Rate Reduction</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {results.qualityImprovement}%
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                Enter your parameters and calculate to see projected savings
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};